import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, updateDoc, collectionGroup, query, where } from "firebase/firestore";
import { db } from "@/firebase";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { Organization, OrgAccess, Website, PlanType } from "@/types";
import { ClientRecord } from "@/types/client";
import { addOrganizations, updateOrganizationSettings } from "../slice";
import { RootState } from "@reduxjs/toolkit/dist/query/core/apiState";
import { OrganizationSettingsForm } from "@/pages/organization-settings/OrganizationSettings";
import { ApiError } from "@/types/api";
import pick from "lodash/pick";
import { pickBy } from "lodash";

export const organizationsApi = createApi({
  reducerPath: "userOrganizations",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["userOrganizations"],
  endpoints: (builder) => ({
    fetchUserOrganizations: builder.query<Organization[], OrgAccess[] | SkipToken>({
      queryFn: async (userAccess, { dispatch }) => {
        const TASK = "Comercios del usuario";
        const organizations: Organization[] = [];
        const userOrganizationsIds = userAccess.map(({ id }) => id);

        try {
          dispatch(addLoadingTask(TASK));

          for await (const organizationId of userOrganizationsIds) {
            try {
              const docRef = doc(db, "organizations", organizationId);
              const snapshot = await getDoc(docRef);

              if (snapshot.exists()) {
                const branchesRef = collection(db, `organizations/${organizationId}/branches`);
                const branchesSnapshot = await getDocs(branchesRef);
                const branches = branchesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

                organizations.push({
                  ...snapshot.data(),
                  branches,
                  id: snapshot.id,
                } as Organization);
              }
            } catch (err) {
              console.error(`Error loading organization ${organizationId}:`, err);
            }
          }

          dispatch(addOrganizations(organizations));

          return {
            data: organizations || [],
          };
        } catch (error) {
          return {
            error: {
              status: 400,
              statusText: "Error al cargar perfiles de usuario",
            },
          };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      providesTags: ["userOrganizations"],
    }),
    fetchOrganizationsByAlias: builder.query({
      queryFn: async (organizationAlias: string | SkipToken) => {
        try {
          const docRef = doc(db, `aliases/${String(organizationAlias)}`);
          const snapshot = await getDoc(docRef);

          if (snapshot.exists()) {
            return { data: { ...snapshot.data(), id: snapshot.id } };
          } else {
            return {
              error: {
                status: 404,
                statusText: "Organización no encontrada",
              },
            };
          }
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["userOrganizations"],
    }),
    fetchOrganizationsById: builder.query<Organization, string | SkipToken>({
      queryFn: async (organizationId, { dispatch }) => {
        const docRef = doc(db, `organizations/${String(organizationId)}`);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          const organization = { ...snapshot.data(), id: snapshot.id } as Organization;
          dispatch(addOrganizations([organization]));

          return {
            data: organization,
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Comercio no encontrado",
          },
        };
      },
      providesTags: ["userOrganizations"],
    }),
    deleteOrganization: builder.mutation<string, string | SkipToken>({
      queryFn: async (organizationId) => {
        if (!organizationId) {
          return {
            error: {
              status: 400,
              statusText: "Id de comercio no válido",
            },
          };
        }

        const docRef = doc(db, `organizations/${String(organizationId)}`);
        try {
          await deleteDoc(docRef);
        } catch {
          return {
            error: {
              status: 500,
              statusText: "Error al eliminar el comercio",
            },
          };
        }
      },
      invalidatesTags: ["userOrganizations"],
    }),
    fetchOrganizationWebsiteById: builder.query<Website, { organizationId: string | SkipToken; websiteId: string | SkipToken }>({
      queryFn: async ({ organizationId, websiteId }) => {
        const docRef = doc(db, `organizations/${String(organizationId)}/websites/${String(websiteId)}`);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          return {
            data: {
              ...snapshot.data(),
              id: snapshot.id,
            },
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Sitio web no encontrado",
          },
        };
      },
      providesTags: ["userOrganizations"],
    }),
    updateOrganization: builder.mutation<Organization, { organizationId: string; payload: Partial<Organization> }>({
      queryFn: async ({ organizationId, payload }, { getState }) => {
        try {
          const { auth } = getState() as RootState;

          if (!auth?.user?.uid || !organizationId || !payload) {
            return {
              error: {
                status: 400,
                statusText: "Consulta inválida",
              },
            };
          }

          const ALLOWED_KEYS = [
            "title",
            "industry",
            "slogan",
            "logo",
            "isotype",
            "header",
            "primaryColor",
            "secondaryColor",
            "website",
            "email",
            "address",
            "phone",
            "whatsapp",
            "latitude",
            "longitude",
            "settings",
            "facebook",
            "instagram",
            "tiktok",
            "x",
            "alias",
            "googleDomainVerificationToken",
            "isDomainVerifiedByGoogle",
            "domain",
            "googlePlaceId",
            "mainBranch",
            "plan",
            "legal",
            "productAdoptionProfile",
          ];

          const draft = {
            ...pick(payload, ALLOWED_KEYS),
            lastUpdatedBy: auth.user.uid,
            lastUpdatedAt: Date.now(),
          };
          const docRef = doc(db, `organizations/${organizationId}`);
          await updateDoc(docRef, pickBy(draft, v => v !== undefined));

          return {
            data: {
              ...draft,
              id: organizationId,
            },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              statusText: "Error al actualizar la organización",
            },
          };
        }
      },
      invalidatesTags: ["userOrganizations"],
    }),
    updateOrganizationSettings: builder.mutation<Organization, { organizationId: string; payload: OrganizationSettingsForm }>({
      queryFn: async ({ organizationId, payload }, { getState, dispatch }) => {
        try {
          const { auth } = getState() as RootState;

          if (!auth?.user?.uid || !organizationId || !payload) {
            return {
              error: {
                status: 400,
                statusText: "Consulta inválida",
              },
            };
          }

          const draft = {
            ...payload,
            lastUpdatedBy: auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          const removeUndefinedDeep = (obj: any): any => {
            if (Array.isArray(obj)) {
              return obj.map(removeUndefinedDeep);
            }
            if (obj !== null && typeof obj === "object") {
              return Object.entries(obj).reduce((acc, [key, value]) => {
                if (value !== undefined) {
                  acc[key] = removeUndefinedDeep(value);
                }
                return acc;
              }, {} as any);
            }
            return obj;
          };

          const sanitizedDraft = removeUndefinedDeep(draft);

          const docRef = doc(db, `organizations/${organizationId}`);
          await updateDoc(docRef, sanitizedDraft);
          dispatch(updateOrganizationSettings({ organizationId, settings: sanitizedDraft?.settings ?? {} }));

          return {
            data: {
              ...sanitizedDraft,
              id: organizationId,
            },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              statusText: "Error al actualizar la organización",
            },
          };
        }
      },
      invalidatesTags: ["userOrganizations"],
    }),
    createOrganization: builder.mutation<Organization, Partial<Organization>>({
      queryFn: async (payload, { getState }) => {
        const { auth } = getState() as RootState;

        if (!auth?.user?.uid) {
          return {
            error: {
              status: 401,
              statusText: "No autorizado",
            },
          };
        }

        const ALLOWED_KEYS = [
          "createdBy",
          "createdAt",
          "title",
          "industry",
          "slogan",
          "logo",
          "isotype",
          "header",
          "primaryColor",
          "secondaryColor",
          "website",
          "email",
          "address",
          "phone",
          "whatsapp",
          "latitude",
          "longitude",
          "settings",
          "facebook",
          "instagram",
          "x",
          "tiktok",
          "alias",
          "googleDomainVerificationToken",
          "isDomainVerifiedByGoogle",
          "googlePlaceId",
          "mainBranch",
          "plan",
          "legal"
        ];

        const draft = {
          ...pick(payload, ALLOWED_KEYS),
          createdBy: auth.user.uid,
          createdAt: Date.now(),
        };

        const collectionRef = collection(db, "organizations");
        const sanitizedDraft = pickBy(draft, v => v !== undefined);
        const snapshot = await addDoc(collectionRef, sanitizedDraft);

        if (snapshot.id) {
          return {
            data: {
              ...sanitizedDraft,
              id: snapshot.id,
            } as Organization,
          };
        }

        return {
          error: {
            status: 500,
            statusText: "Error al crear la organización",
          },
        };
      },
      invalidatesTags: ["userOrganizations"],
    }),
    fetchClientsGrid: builder.query<ClientRecord[], void>({
      queryFn: async () => {
        try {
          const [organizationsSnap, accessSnap] = await Promise.all([
            getDocs(collection(db, "organizations")),
            getDocs(
              query(
                collectionGroup(db, "access"),
                where("role", "in", ["owner", "admin", "super-admin"])
              )
            ),
          ]);

          const authenticatedOrganizations = new Map<
            string,
            {
              uid: string;
              userName?: string;
              userEmail?: string;
            }
          >();

          accessSnap.docs.forEach((docSnap) => {
            const orgId = docSnap.ref.parent.parent?.id;

            if (!orgId) return;

            if (!authenticatedOrganizations.has(orgId)) {
              const data = docSnap.data();

              authenticatedOrganizations.set(orgId, {
                uid: docSnap.id,
                userName: data.userName,
                userEmail: data.userEmail,
              });
            }
          });

          const results: ClientRecord[] = [];

          organizationsSnap.docs.forEach((orgDoc) => {
            const org = orgDoc.data() as Organization;
            const organizationId = orgDoc.id;

            const access =
              authenticatedOrganizations.get(organizationId);

            let onboardingStage: ClientRecord["onboardingStage"];

            if (!org.title) {
              onboardingStage = "Registrado";
            }
            else if (!org.alias) {
              onboardingStage = "Organización Creada";
            }
            else if (!access) {
              onboardingStage = "Autenticado";
            }
            else {
              onboardingStage = "Completado";
            }

            results.push({
              uid: access?.uid ?? organizationId,
              name:
                access?.userName ||
                org.title ||
                "Sin nombre",

              email:
                access?.userEmail ||
                org.email ||
                "",

              emailVerified: true,

              createdAt:
                org.createdAt ??
                0,

              organizationTitle:
                org.title ||
                "Sin nombre",

              organizationId,

              onboardingStage,

              plan:
                org.plan === PlanType.PRO
                  ? PlanType.PRO
                  : PlanType.FREE,
            });
          });

          results.sort(
            (a, b) =>
              Number(b.createdAt ?? 0) -
              Number(a.createdAt ?? 0)
          );

          return {
            data: results,
          };
        } catch (error: any) {
          console.error(
            "Error in fetchClientsGrid query:",
            error
          );

          return {
            error: {
              status: 500,
              statusText:
                error.message ||
                "Error al cargar clientes",
            },
          };
        }
      },

      providesTags: ["userOrganizations"],
    }),
  }),
});

export const {
  useFetchUserOrganizationsQuery,
  useLazyFetchUserOrganizationsQuery,
  useUpdateOrganizationMutation,
  useUpdateOrganizationSettingsMutation,
  useFetchOrganizationsByAliasQuery,
  useLazyFetchOrganizationsByAliasQuery,
  useFetchOrganizationsByIdQuery,
  useFetchOrganizationWebsiteByIdQuery,
  useCreateOrganizationMutation,
  useDeleteOrganizationMutation,
  useFetchClientsGridQuery,
} = organizationsApi;
